import {
  Box,
  Typography,
  Checkbox,
  useTheme,
  TextField,
  InputAdornment,
  Link,
  Tooltip,
} from "@mui/material";
import { FC, useMemo, useState, useEffect } from "react";
import {
  CompletedOrder,
  InProgressLocationIcon,
  makeSxStyles,
  MissedLocationIcon,
  PartiallyCompleted,
  PlowIcon,
  SkippedStatusIcon,
  generateRoute,
} from "ui";
// import { AdminRegionLocationAddressesDto } from "../../../state/region-management-store";
import { MapMarker, OrderStatuses } from "../../../components/map/map.types";
import { showCustomerStatus, showCustomerType } from "../route-planner/helpers";
import {
  formatNumberToMinutes,
  formatNumberToMinutesPrecise,
  formattedValueWithSymbol,
  paletteColorStringRenderer,
} from "../../../utils/string-helpers";
import { RouteStatus } from "../route-planner/helpers/route-status.enum";
import { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";
import { DateTime } from "luxon";
import { CheckOutlined, Close } from "@mui/icons-material";
import {
  MeasurementSystemEnum,
  useAdminLocationControllerUpdateServiceTime,
  useAdminRouteV2ControllerUpdateRouteCustomerOrders,
} from "service";
import useGlobalStore from "../../../state/global-store";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import { SelectedAddressForNotes } from "../../../components/map/selected-addresses-sidebar";
import useRoutePlannerStore from "../../../state/route-planner-store";
import { AdRoutes } from "../../../constants/ad-routes";
import {
  numericFieldSx,
  numericInputProps,
} from "@/shared/utils/numeric-input";

const ADDRESS_CARD_TITLE_COLOR = "#C1C7CE";
const ADDRESS_CARD_VALUE_COLOR = "#C5C6C9";
const CHECKMARK_BACKGROUND_COLOR = "#8ACEFF";

interface AddressProps extends MapMarker {
  onHandleChange: (val: string) => void;
  onViewNotes?: (selectedAddress: SelectedAddressForNotes) => void;
  view: "route-planner" | "region-management" | "notification-recipients";
  serviceTimeMap?: Record<string, number>;
  adjustedServiceTimeMap?: Record<string, number>;
  adjustedEstimatedTimeMinutesMap?: Record<string, number>;
  currentRouteStatus?: RouteStatus; // For precise formatting in live phase
}

const AddressBoxComponent: FC<AddressProps> = (props) => {
  const styles = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { date } = useParams();
  const { selectedAddresses, updateSelectedAddresses } = useRoutePlannerStore();
  const { system } = useGlobalStore();

  // Use real-time adjusted service time first, then calculated service time map,
  // and finally fall back to props.duration.
  const calculatedServiceDuration =
    props.adjustedServiceTimeMap?.[props.id] ??
    props.serviceTimeMap?.[props.id] ??
    props?.duration;

  const [isEditingServiceDuration, setIsEditingServiceDuration] =
    useState(false);
  const [serviceDurationNumber, setServiceDurationNumber] = useState<
    string | undefined
  >(calculatedServiceDuration as string);
  const { palette } = theme;

  // Use precise formatting in live phase
  const isLivePhase = props.currentRouteStatus === RouteStatus.Live;
  const formatTime = isLivePhase
    ? formatNumberToMinutesPrecise
    : formatNumberToMinutes;

  // Helper function to calculate estimated arrival time
  // In live phase, show seconds for precision
  const calculateEstimatedArrival = (
    routeStartTime?: string,
    estimatedTimeMinutes?: number,
    locationSequence?: number | null,
  ): string | null => {
    if (!routeStartTime) {
      return null;
    }

    // Time format: show seconds in live phase for precision
    const timeFormat = isLivePhase ? "h:mm:ss a" : "h:mm a";

    // Special case: If this is the first address and estimatedTimeMinutes is 0 or not present,
    // show the route start time as the estimated arrival
    if (
      locationSequence === 1 &&
      (!estimatedTimeMinutes || estimatedTimeMinutes <= 0)
    ) {
      try {
        const [hours, minutes] = routeStartTime.split(":").map(Number);
        const startTime = DateTime.now().set({
          hour: hours,
          minute: minutes,
          second: 0,
        });
        return startTime.toFormat(timeFormat);
      } catch {
        return null;
      }
    }

    // For all other cases: return null if estimatedTimeMinutes is not valid
    if (!estimatedTimeMinutes || estimatedTimeMinutes <= 0) {
      return null;
    }

    try {
      // Parse the route start time (format: "HH:mm")
      const [hours, minutes] = routeStartTime.split(":").map(Number);
      const startTime = DateTime.now().set({
        hour: hours,
        minute: minutes,
        second: 0,
      });

      // Add estimated time - convert minutes to seconds for precision
      const totalSeconds = Math.round(estimatedTimeMinutes * 60);
      const arrivalTime = startTime.plus({ seconds: totalSeconds });

      // Format based on phase
      return arrivalTime.toFormat(timeFormat);
    } catch {
      return null;
    }
  };

  const effectiveEstimatedTimeMinutes =
    props.adjustedEstimatedTimeMinutesMap?.[props.id] ??
    props.travelDetails?.estimatedTimeMinutes;

  const estimatedArrival = calculateEstimatedArrival(
    props.routeStartTime,
    effectiveEstimatedTimeMinutes,
    props.locationSequence,
  );

  const getDriverStatusDetails = (
    status?: AdminGetRoutesLocationsResDto["driverStatus"],
  ) => {
    switch (status) {
      case "toBePlowed":
        return {
          text: "To Be Plowed",
          color: "#FFFFFF",
          icon: <PlowIcon />,
        };
      case "completed":
        return {
          text: "Order was completed",
          color: "#60DBB9",
          icon: <CompletedOrder fill="#60DBB9" />,
        };
      case "partiallyCompleted":
        return {
          text: "Order was Partially Completed",
          color: "#FFE2AB",
          icon: <PartiallyCompleted fill="#FFE2AB" />,
        };
      case "cantPlow":
        return {
          text: "Order couldn't be plowed",
          color: "#FFB4AB",
          icon: <MissedLocationIcon fill="#FFB4AB" />,
        };
      case "inProgress":
        return {
          text: "Order is in progress...",
          color: "#FFE2AB",
          icon: <InProgressLocationIcon fill="#FFE2AB" />,
        };
      default:
        return {
          text: "Unknown Status",
          color: "gray",
          icon: null,
        };
    }
  };

  const formatDriverStatusChangeReason = (
    reason?: AdminGetRoutesLocationsResDto["driverStatusChangeReason"],
  ): string => {
    switch (reason) {
      case "carBlockingDriveway":
        return "Car Blocking Driveway";
      case "obstructionBlockingDriveway":
        return "Obstruction Blocking Driveway";
      case "alreadyCleared":
        return "Already Cleared";
      case "other":
        return "Other";
      default:
        return "";
    }
  };

  const submitEditServiceDuration = () => {
    setIsEditingServiceDuration(false);
    updateServiceTime({
      pathParams: {
        locationId: props.id,
      },
      body: {
        serviceTime: Number(serviceDurationNumber),
      },
    });
  };

  const showServiceDurationField = useMemo(() => {
    if (!isEditingServiceDuration)
      return (
        <Box
          sx={styles.serviceDuration}
          onClick={() => setIsEditingServiceDuration(true)}
        >
          {formatTime(Number(serviceDurationNumber))}
        </Box>
      );

    return (
      <Box sx={styles.serviceDurationWrapper}>
        <TextField
          id="serviceTime"
          type="number"
          margin="dense"
          variant="outlined"
          size="small"
          value={serviceDurationNumber}
          onChange={(evt) => {
            const value = Number(evt.target.value);
            if (value >= 1 && value <= 300) {
              setServiceDurationNumber(evt.target.value);
            } else if (evt.target.value === "" || evt.target.value === "0") {
              setServiceDurationNumber(evt.target.value);
            }
          }}
          error={
            Number(serviceDurationNumber) === 0 || serviceDurationNumber === ""
          }
          helperText={
            Number(serviceDurationNumber) === 0 || serviceDurationNumber === ""
              ? "Service duration must be at least 1 minute"
              : ""
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">min</InputAdornment>,
            // Input guards only — this editor keeps its own local string state
            // and range check (1–300) rather than going through Formik.
            inputProps: {
              ...numericInputProps({ allowDecimal: true }),
              min: 1,
            },
          }}
          sx={numericFieldSx}
        />
        <Box sx={styles.serviceDurationActions}>
          <Close
            sx={{ color: "#8ACEFF" }}
            width={7}
            height={7}
            onClick={() => {
              setServiceDurationNumber(calculatedServiceDuration as string);
              setIsEditingServiceDuration(false);
            }}
          />
          <CheckOutlined
            sx={{
              color:
                Number(serviceDurationNumber) === 0 || !serviceDurationNumber
                  ? "#ccc"
                  : "#00344E",
              cursor:
                Number(serviceDurationNumber) === 0 || !serviceDurationNumber
                  ? "not-allowed"
                  : "pointer",
            }}
            width={7}
            height={7}
            onClick={() => {
              if (Number(serviceDurationNumber) > 0) {
                submitEditServiceDuration();
              }
            }}
          />
        </Box>
      </Box>
    );
  }, [
    isEditingServiceDuration,
    serviceDurationNumber,
    calculatedServiceDuration,
  ]);

  const driverStatusDetails = getDriverStatusDetails(props.driverStatus);

  // Order-level status: a skipped order is excluded from the active route and
  // gets the dedicated "To Be Skipped" header + "Add to Route" action.
  const isSkipped = props.status === OrderStatuses.Skipped;

  // Customer order status can only be changed while the route is in the planning
  // phase (the backend rejects it in build/planned/live/ended states).
  const canAddToRoute = props.currentRouteStatus === RouteStatus.Planning;

  // Region-colored sequence badge (hidden for skipped orders, which aren't
  // sequenced). Use the base region color (not the darker "selected" shade),
  // to match the Figma badge.
  const sequenceBadgeColor =
    paletteColorStringRenderer(
      palette,
      (props.region?.color ?? props.color)?.toLowerCase(),
      false,
    ) ??
    props.region?.color ??
    props.color;

  // Formatted driver-status change reason ("" when there is none).
  const driverStatusChangeReasonText = formatDriverStatusChangeReason(
    props.driverStatusChangeReason,
  );

  const { mutate: addToRoute, isLoading: isAddingToRoute } =
    useAdminRouteV2ControllerUpdateRouteCustomerOrders({
      onSuccess: () => {
        toast.success("Order added to route!");

        // Invalidate by path prefix so BOTH the filtered (map/sidebar) and the
        // unfiltered (table/skipped-count) route queries refetch. A key that
        // hardcodes `orderStatuses: []` only matches the filtered query.
        queryClient.invalidateQueries(["api", "v2", "admin", "routes"]);
      },
    });

  const handleAddToRoute = () => {
    if (!date || !canAddToRoute) return;

    addToRoute({
      body: { locations: [props.id], status: "regular" },
      pathParams: {
        date: DateTime.fromFormat(date, "MM-dd-yyyy").toFormat("yyyy-MM-dd"),
      },
    });
  };

  // EST Snow — formatted in the user's selected measurement system.
  const estSnow = props.snowAmount
    ? formattedValueWithSymbol(
        system === MeasurementSystemEnum.metric
          ? props.snowAmount.metric
          : props.snowAmount.imperial,
        system,
      )
    : null;

  const { mutate: updateServiceTime } =
    useAdminLocationControllerUpdateServiceTime({
      onSuccess: () => {
        toast.success(`Successfully updated the service time!`);

        if (!date) return;

        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v2/admin/routes",
            operationId: "adminRouteV2ControllerGetRoutes",
            variables: {
              queryParams: {
                date: DateTime.fromFormat(date, "MM-dd-yyyy").toFormat(
                  "yyyy-MM-dd",
                ),
                includeLocations: true,
                orderStatuses: [],
              },
            },
          }),
        );

        updateSelectedAddresses(
          selectedAddresses.map((address) => {
            if (address.id === props.id) {
              return {
                ...address,
                duration: serviceDurationNumber,
              };
            }

            return address;
          }),
        );
      },
    });

  // Update serviceDurationNumber when calculatedServiceDuration changes
  useEffect(() => {
    if (!isEditingServiceDuration) {
      setServiceDurationNumber(calculatedServiceDuration as string);
    }
  }, [calculatedServiceDuration, isEditingServiceDuration]);

  return (
    <Box sx={styles.addressBoxCard}>
      <Box sx={styles.headerRow}>
        <Checkbox
          id={props.address}
          checked={props.selected || false}
          sx={styles.headerCheckbox}
          onChange={() => props.onHandleChange(props.id)}
        />
        <Box sx={styles.headerText}>
          {props.customer?.id ? (
            <Link
              component="button"
              sx={styles.addressStyle}
              data-testid="address-box-address"
              onClick={() =>
                navigate(
                  generateRoute(AdRoutes.customerDetailAddress, {
                    customerId: props.customer!.id,
                    addressId: props.id,
                  }),
                )
              }
            >
              {props.address}
            </Link>
          ) : (
            <Typography
              sx={styles.addressStyle}
              data-testid="address-box-address"
            >
              {props.address}
            </Typography>
          )}
          <Typography sx={styles.customerName}>
            {props.customer?.firstName} {props.customer?.lastName}
          </Typography>
        </Box>
      </Box>
      {props.view === "route-planner" && (
        <Box sx={styles.driverStatus}>
          <Box sx={styles.driverStatusRow}>
            <Box sx={styles.driverStatusDetails}>
              <Typography
                variant="labelLarge"
                sx={{
                  fontWeight: 500,
                  color: isSkipped ? "#E2E2E5" : driverStatusDetails.color,
                }}
              >
                {isSkipped ? "To Be Skipped" : driverStatusDetails.text}
              </Typography>

              {isSkipped ? (
                <Typography sx={styles.lastPlowed}>
                  Last Plowed:{" "}
                  {props.lastPlowedAt
                    ? DateTime.fromISO(props.lastPlowedAt).toFormat(
                        "MM/dd/yyyy",
                      )
                    : "N/A"}
                </Typography>
              ) : (
                <>
                  {props.driverStatusLastUpdatedAt && (
                    <Typography
                      variant="labelMedium"
                      sx={{ color: palette.grey[300] }}
                    >
                      {DateTime.fromISO(
                        props.driverStatusLastUpdatedAt,
                      ).toFormat("MM/dd/yyyy, hh:mm a")}
                    </Typography>
                  )}
                  {!!driverStatusChangeReasonText && (
                    <Typography
                      variant="labelMedium"
                      fontWeight="600"
                      sx={{ color: palette.grey[100] }}
                    >
                      Reason: {driverStatusChangeReasonText}
                    </Typography>
                  )}
                  {props.driverStatusChangeReasonComment && (
                    <Typography
                      variant="labelMedium"
                      sx={{ color: palette.grey[100] }}
                    >
                      Comment: {props.driverStatusChangeReasonComment}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            <Box sx={styles.driverStatusIcons}>
              <Box sx={styles.statusIcon}>
                {isSkipped ? <SkippedStatusIcon /> : driverStatusDetails.icon}
              </Box>
              {!isSkipped && (props.locationSequence ?? 0) > 0 && (
                <Box
                  sx={{
                    ...styles.sequenceBadge,
                    backgroundColor: sequenceBadgeColor,
                  }}
                  data-testid="address-box-sequence-badge"
                >
                  {props.locationSequence}
                </Box>
              )}
            </Box>
          </Box>
          {isSkipped && (
            <Tooltip
              title={
                canAddToRoute
                  ? ""
                  : "Orders can only be added to the route during the planning phase."
              }
            >
              <Box component="span" sx={{ alignSelf: "flex-start" }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleAddToRoute}
                  disabled={isAddingToRoute || !canAddToRoute}
                  sx={styles.addToRoute}
                  data-testid="address-box-add-to-route"
                >
                  Add to Route
                </Link>
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
      {props.region && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>Region:</Typography>
          <label>{props.region.name}</label>
        </Box>
      )}
      {props.view === "route-planner" && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>Operator:</Typography>
          <label>{props.driverName ?? "Unassigned"}</label>
        </Box>
      )}
      {estimatedArrival &&
        !isSkipped &&
        props.driverStatus === "toBePlowed" &&
        props.view !== "notification-recipients" && (
          <Box sx={styles.addressRowItem}>
            <Typography sx={styles.addressRowTitle}>EST Arrival:</Typography>
            <label>{estimatedArrival}</label>
          </Box>
        )}
      {props.view !== "notification-recipients" && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>Service Duration:</Typography>
          {showServiceDurationField}
        </Box>
      )}
      {estSnow && props.view !== "notification-recipients" && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>EST Snow:</Typography>
          <label>{estSnow}</label>
        </Box>
      )}
      {props?.status && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>Status:</Typography>
          <label>{showCustomerStatus(props.status)}</label>
        </Box>
      )}
      {props?.customerType && (
        <Box sx={styles.addressRowItem}>
          <Typography sx={styles.addressRowTitle}>Type:</Typography>
          <Box sx={styles.typeRateButton}>
            {showCustomerType(props.customerType)}
          </Box>
        </Box>
      )}
      {props.view !== "notification-recipients" && (
        <Link
          component="button"
          variant="body2"
          onClick={() =>
            props.onViewNotes?.({
              id: props.id,
              address: props.address,
            })
          }
          sx={{
            marginTop: "16px",
            color: "#8ACEFF",
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: 500,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View Notes ({props.locationNoteCount})
        </Link>
      )}
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  addressBoxCard: {
    width: "100%",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: palette.grey[600],
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  headerRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "16px",
  },
  headerCheckbox: {
    padding: "0",
    flexShrink: 0,
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
    },
    "&.Mui-checked": {
      color: CHECKMARK_BACKGROUND_COLOR,
    },
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: "1 1 0",
    minWidth: 0,
  },
  addressStyle: {
    color: palette.primary.main,
    fontWeight: "600",
    fontSize: "14px",
    lineHeight: "20px",
    textAlign: "left",
    padding: 0,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  customerName: {
    color: ADDRESS_CARD_TITLE_COLOR,
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    textAlign: "left",
  },
  addressRowItem: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
    fontSize: "14px",
    width: "100%",

    "& label, & .address-card-value": {
      color: ADDRESS_CARD_VALUE_COLOR,
      letterSpacing: "0.1px",
    },
  },
  addressRowTitle: {
    color: ADDRESS_CARD_TITLE_COLOR,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
  },
  typeRateButton: {
    padding: "4px 8px",
    borderRadius: "8px",
    backgroundColor: "#C5C6C926",
    color: ADDRESS_CARD_VALUE_COLOR,
    lineHeight: "16px",
    fontSize: "12px",
  },
  driverStatus: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    marginBottom: "16px",
    borderTop: `1px solid ${palette.grey[400]}`,
    borderBottom: `1px solid ${palette.grey[400]}`,
    paddingY: "16px",
    gap: "16px",
  },
  driverStatusRow: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },
  driverStatusDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  driverStatusIcons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusIcon: {
    display: "flex",
    alignItems: "center",
  },
  sequenceBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    padding: "2px 6px",
    borderRadius: "4px",
    color: "#fff",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
  },
  addToRoute: {
    alignSelf: "flex-start",
    color: "#8ACEFF",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  lastPlowed: {
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: 500,
    color: ADDRESS_CARD_TITLE_COLOR,
  },
  serviceDurationWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    border: "1px solid #8ACEFF",
    borderRadius: "4px",
    padding: "6px 8px",
    width: "50%",

    "& fieldset": {
      borderWidth: "0",
    },
  },
  serviceDurationActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "4px",

    "& > svg": {
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      cursor: "pointer",
    },

    "& > svg:nth-of-type(1)": {
      background: "rgba(202, 196, 208, 0.08)",
    },

    "& > svg:nth-of-type(2)": {
      background: CHECKMARK_BACKGROUND_COLOR,
    },
  },
  serviceDuration: {
    color: ADDRESS_CARD_VALUE_COLOR,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    cursor: "pointer",
    borderRadius: "4px",
    padding: "0 4px",
    marginRight: "-4px",
    "&:hover": {
      backgroundColor: "rgba(202, 196, 208, 0.08)",
    },
  },
}));

export default AddressBoxComponent;
